import { PencilSquareIcon, TrashIcon, WindowIcon } from '@heroicons/react/20/solid'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import {
  ArrowUpOnSquareIcon,
  CloudArrowDownIcon,
  DocumentMagnifyingGlassIcon,
  PauseCircleIcon
} from '@heroicons/react/24/solid'
import { ComponentProps, ReactNode, useState } from 'react'

import Menu, { MenuItem } from '@/components/TailwindUI/Menu'
import cn from '@/utilities/cn'

type AppEditMenuProps = {
  icon?: ReactNode
  readOnly?: boolean
  hasEditPermissions?: boolean
  hasUpdateAvailable?: boolean
  status?: string
  onOpen?: () => void
  onEdit?: () => void
  onExport?: () => void
  onDelete?: () => void
  onViewLogs?: () => void
  onDeactivate?: () => void
  onUpdateApp?: () => void
}

export default function AppEditMenu({
  icon,
  readOnly = true,
  hasEditPermissions = false,
  status,
  onOpen,
  onEdit,
  onExport,
  onDelete,
  onViewLogs,
  onDeactivate,
  onUpdateApp,
  hasUpdateAvailable,
  ...rest
}: AppEditMenuProps & ComponentProps<'button'>) {
  const [direction, setDirection] = useState<'up' | 'down'>('down')
  return (
    <Menu
      className={cn({ hidden: readOnly })}
      icon={icon ?? <EllipsisVerticalIcon className="h-7 w-7 stroke-zinc-400 hover:stroke-zinc-400" />}
      onClick={e => {
        e.stopPropagation()
        // If overflows bottom of screen, switch direction the menu opens
        if (window.screen.availHeight - e.clientY < 350) {
          setDirection('up')
        } else {
          setDirection('down')
        }
      }}
      direction={direction}
      {...rest}
    >
      <div className="py-1">
        {onOpen && (
          <MenuItem
            onClick={e => {
              e.stopPropagation()
              onOpen()
            }}
          >
            <WindowIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
            Open
          </MenuItem>
        )}

        {hasEditPermissions && (
          <>
            {onEdit && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  onEdit()
                }}
              >
                <PencilSquareIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                Edit
              </MenuItem>
            )}
            {onExport && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  onExport?.()
                }}
              >
                <ArrowUpOnSquareIcon
                  className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
                Export
              </MenuItem>
            )}
            {hasUpdateAvailable && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  onUpdateApp?.()
                }}
              >
                <CloudArrowDownIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" title="Update" />
                Update
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  onDelete?.()
                }}
              >
                <TrashIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                Delete
              </MenuItem>
            )}
          </>
        )}
      </div>
      {status === 'active' && hasEditPermissions && onDeactivate && (
        <div className="py-1">
          <MenuItem
            onClick={e => {
              e.stopPropagation()
              onDeactivate()
            }}
          >
            <PauseCircleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
            Deactivate
          </MenuItem>
        </div>
      )}
      {onViewLogs && (
        <div className="py-1">
          <MenuItem
            onClick={e => {
              e.stopPropagation()
              onViewLogs?.()
            }}
          >
            <DocumentMagnifyingGlassIcon
              className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
              aria-hidden="true"
            />
            Notification Logs
          </MenuItem>
        </div>
      )}
    </Menu>
  )
}
